/**
 * Timestamps in links to a moment in an episode (`?t=`).
 * Accepts `734`, `12:14`, `1:02:03`, `12m14s` and `1h2m3s`.
 */

const UNITS: Record<string, number> = { h: 3600, m: 60, s: 1 };

/** Seconds for a `t` value, or null when it is missing or not a timestamp. Clamped to the duration when known. */
export const parseTimestampParam = (value: string | null | undefined, duration?: number): number | null => {
    const text = value?.trim().toLowerCase();
    if (!text) return null;

    let seconds: number | null = null;

    if (/^\d+$/.test(text)) {
        seconds = Number(text);
    } else if (/^\d{1,3}(:\d{1,2}){1,2}$/.test(text)) {
        const parts = text.split(":").map(Number);
        if (parts.slice(1).some((part) => part > 59)) return null;
        seconds = parts.reduce((total, part) => total * 60 + part, 0);
    } else if (/^(\d+h)?(\d+m)?(\d+s)?$/.test(text)) {
        seconds = 0;
        for (const [, amount, unit] of text.matchAll(/(\d+)([hms])/g)) {
            seconds += Number(amount) * UNITS[unit];
        }
    }

    if (seconds === null || !Number.isFinite(seconds)) return null;
    if (duration && duration > 0 && seconds > duration) return Math.floor(duration);
    return seconds;
};

/** The value for `?t=`: whole seconds */
export const buildTimestampParam = (seconds: number): string => String(Math.max(0, Math.floor(seconds)));

/** `12:14` or `1:02:03` for showing a position */
export const formatTimestamp = (seconds: number): string => {
    const total = Math.max(0, Math.floor(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
    return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
};
